import type { LeapCaseCorrespondenceInput } from '@pages/leap-host-connection/LeapCaseCorrespondence/types'
import type {
  LeapAttachmentDto,
  LeapImportReceiptInput,
  LeapMatterDocumentImportInput,
  LeapMatterDocumentSearchInput,
} from '@pages/leap-host-connection/LeapMatterDocuments/types'
import { FROM_MODULE_TYPE } from '@utils/constants'

import { ApiRequest } from '../baseService'

import type { PaperworkLinkDto } from './types/PaperworkLinkDto'

const BaseRoute = 'app/leap-app'

// The Disbursement-Funding-only fields the panel sends on create and on a detail save. Every
// field is optional on the wire — whatever the caller omits is filled by the backend's
// DisbursementFundingCreateDefaults on create, and left unchanged on a save.
export interface LeapDisbursementFundingInput {
  fundingAgreementId?: string
  // Overrides the funding agreement's default repayment period; omitted keeps the default.
  repaymentPeriodId?: string
  clientRequestNo?: string
  payeeType?: number
  payeeTransactionProviderId?: string
  // Advance-request fields. CREATE ONLY — the backend's save path reads the sub-object but
  // never writes these, so sending them on a save has no effect.
  providerInvoiceNo?: string
  providerInvoiceDate?: string
  requestedAdvanceAmountExclTax?: number
  requestedAdvanceAmountTax?: number
  advanceDescription?: string
  // No requestedAdvanceAmount (incl Tax): the backend derives it from the two values above,
  // because every LEAP endpoint is anonymous and a client-supplied total could disagree.

  // Funding Method fields, sent only when the chosen facility agreement is a Loan. The backend
  // clears whichever half of the block does not apply to the agreement
  // (LeapDisbursementFundingGuards.Sanitise), so sending a stale value is harmless.
  // interestType is deliberately absent: it belongs to the agreement and the panel renders it
  // read-only, so it is not writable over an anonymous endpoint.
  repaymentMethod?: number
  interestRateValue?: number
  // Monthly-repayment-only. Cleared by the backend when the method is One-Off.
  repaymentAmount?: number
  dueDayInMonth?: number
  penaltyForLatePayment?: number
  penaltyForLatePaymentType?: number
  penaltyDescription?: string
}

export interface LeapCreateServicesInput {
  matterId: string
  ClientId: string
  CompanyId?: string
  companyPmsId?: string
  CaseManagerId?: string
  MatterTypeId?: string
  typeOfReportId?: string
  CentreId?: string
  matterFileNumber: string
  tenantId: string
  fromModule: FROM_MODULE_TYPE
  attachments?: LeapAttachmentDto[]
  importReceipts?: LeapImportReceiptInput[]
  sessionIds?: string[]
  paperworkLinks?: PaperworkLinkDto[]
  paperworkOtherLinks?: PaperworkLinkDto[]
  disbursementFunding?: LeapDisbursementFundingInput
  // False for Save, true for Send Request. The backend maps it onto each module's own notify
  // flag; omitting it means Save.
  sendRequest?: boolean
}

export interface LeapCreateServicePreviewInput {
  matterId: string
  companyPmsId?: string
  userId?: string
  tenantId: string
  matterFileNumber: string
  fromModule: FROM_MODULE_TYPE
}

export interface LeapUpdateLinkedCaseDetailInput {
  tenantId: string
  companyPmsId: string
  caseId: string
  fromModule: string
  paperworkLinks?: PaperworkLinkDto[]
  paperworkOtherLinks?: PaperworkLinkDto[]
  attachments?: LeapAttachmentDto[]
  importReceipts?: LeapImportReceiptInput[]
  cRFilesExLink?: string
  passwordCRFilesExLink?: string
  disbursementFunding?: LeapDisbursementFundingInput
  // Type Of Claim. Undefined means "not submitted, leave unchanged"; the all-zero Guid string
  // means "cleared". Only the Disbursement Funding branch reads it today.
  matterTypeId?: string
}

// Sends the request mail for a case that was created with Save.
export interface LeapSendServiceRequestInput {
  tenantId: string
  companyPmsId: string
  caseId: string
  fromModule: string
}

const getAuthHeader = (accessToken?: string) => {
  if (!accessToken) {
    return undefined
  }

  const normalizedToken = accessToken.startsWith('Bearer ')
    ? accessToken
    : `Bearer ${accessToken}`

  // Dedicated header so the LEAP token never collides with the system JWT
  // that ApiRequest attaches to Authorization by default (see baseService.ts).
  return {
    'Leap-Authorization': normalizedToken,
  }
}

const GetPmsConfig = (
  matterId: string | null | undefined,
  accessToken?: string
) => {
  const url = matterId
    ? `${BaseRoute}/pms-config?matterId=${matterId}`
    : `${BaseRoute}/pms-config`
  return ApiRequest.post(url, {}, { headers: getAuthHeader(accessToken) })
}

const GetMatterDetail = (id, tenantId, companyPMSId = null) => {
  return ApiRequest.get(
    `${BaseRoute}/matters/${id}?tenantId=${tenantId ?? ''}&companyPMSId=${
      companyPMSId ?? ''
    }`
  )
}

const GetMatters = (companyPMSId = null) => {
  return ApiRequest.post(`${BaseRoute}/matters`, {
    method: 'Get',
    companyPMSId,
  })
}

const GetPersons = (filter, companyPMSId = null) => {
  return ApiRequest.post(`${BaseRoute}/persons`, {
    method: 'Get',
    data: filter?.query,
    companyPMSId,
  })
}

const GetMatterAccount = (
  responsibleUserId,
  clientRecordId = null,
  companyPMSId = null
) => {
  return ApiRequest.get(
    `${BaseRoute}/mattersAccount?responsibleUserId=${
      responsibleUserId ?? ''
    }&clientRecordId=${clientRecordId ?? ''}&companyPMSId=${companyPMSId ?? ''}`
  )
}

const CreateServices = (
  input: LeapCreateServicesInput,
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/create-services`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const CreateServicesByMatterIdData = (
  input: LeapCreateServicePreviewInput,
  accessToken?: string
) => {
  return ApiRequest.post(
    `${BaseRoute}/create-services-by-matterId-data`,
    input,
    {
      headers: getAuthHeader(accessToken),
    }
  )
}

const GetTypeOfClaim = (claimTypeId: string, companyPMSId?: string) => {
  return ApiRequest.get(`${BaseRoute}/type-of-claim`, {
    params: {
      claimTypeId,
      companyPMSId: companyPMSId ?? null,
    },
  })
}

const GetLookupConfigs = (
  input: {
    companyPmsId?: string
    type:
      | 'TYPE_OF_CLAIM'
      | 'TYPE_OF_REPORT'
      | 'SPECIALTY'
      | 'SUB_SPECIALTY'
      | 'ACCREDITATION'
    filterText?: string
    maxResultCount?: number
  },
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/lookup-configs`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const GetCentres = (
  input: {
    companyPmsId?: string
    filterText?: string
    maxResultCount?: number
  },
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/centres`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const GetFundingAgreements = (
  input: {
    companyPmsId?: string
    filterText?: string
    maxResultCount?: number
  },
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/funding-agreements`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const GetServiceProviders = (
  input: {
    companyPmsId?: string
    filterText?: string
    maxResultCount?: number
  },
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/service-providers`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const SearchAppointments = (
  input: { companyPmsId: string; filter: Record<string, any> },
  accessToken?: string
) =>
  ApiRequest.post(`${BaseRoute}/search-appointments`, input, {
    headers: getAuthHeader(accessToken),
  })

const SearchSpecialists = (
  input: { companyPmsId: string; filterText?: string; maxResultCount?: number },
  accessToken?: string
) =>
  ApiRequest.post(`${BaseRoute}/search-specialists`, input, {
    headers: getAuthHeader(accessToken),
  })

const SearchPostcodes = (
  input: { companyPmsId: string; filterText?: string; maxResultCount?: number },
  accessToken?: string
) =>
  ApiRequest.post(`${BaseRoute}/search-postcodes`, input, {
    headers: getAuthHeader(accessToken),
  })

const UpdateLinkedCaseDetail = (
  input: LeapUpdateLinkedCaseDetailInput,
  accessToken?: string
) => {
  return ApiRequest.put(`${BaseRoute}/linked-case-detail`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const SendServiceRequest = (
  input: LeapSendServiceRequestInput,
  accessToken?: string
) =>
  ApiRequest.post(`${BaseRoute}/send-service-request`, input, {
    headers: getAuthHeader(accessToken),
  })

const SearchMatterDocuments = (
  input: LeapMatterDocumentSearchInput,
  accessToken?: string
) =>
  ApiRequest.post(`${BaseRoute}/matter-documents/search`, input, {
    headers: getAuthHeader(accessToken),
  })

const ImportMatterDocuments = (
  input: LeapMatterDocumentImportInput,
  accessToken?: string
) =>
  ApiRequest.post(`${BaseRoute}/matter-documents/import`, input, {
    headers: getAuthHeader(accessToken),
  })

const HandleLeapConsent = (companyPMSId: string, isAccepted: boolean) => {
  return ApiRequest.post(`${BaseRoute}/consent`, {
    companyPMSId,
    isAccepted,
  })
}

const GetAllLinkedCases = (
  input: {
    companyPmsId: string
    tenantId: string
    matterId?: string
    filterText?: string
    fromModule?: string
    skipCount?: number
    maxResultCount?: number
  },
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/all-linked-cases`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const GetLinkedCaseDetail = (
  input: { caseId: string; fromModule: string; companyPmsId: string },
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/linked-case-detail-data`, input, {
    headers: getAuthHeader(accessToken),
  })
}

const GetCaseCorrespondences = (
  input: LeapCaseCorrespondenceInput,
  accessToken?: string
) => {
  return ApiRequest.post(`${BaseRoute}/case-correspondence`, input, {
    headers: getAuthHeader(accessToken),
  })
}

// Returns { url }. The panel must then open that URL with a top-level window.open rather than
// fetching it — see triggerAttachmentDownload in LinkedCaseDetailView.tsx for why a fetch or an
// anchor download is blocked inside the LEAP webview.
const CreateCaseCorrespondenceExportLink = (
  input: LeapCaseCorrespondenceInput,
  accessToken?: string
) => {
  return ApiRequest.post(
    `${BaseRoute}/case-correspondence/export-link`,
    input,
    {
      headers: getAuthHeader(accessToken),
    }
  )
}

const GetSessionDetail = (
  sessionId: string,
  companyPmsId: string,
  accessToken?: string
) =>
  ApiRequest.post(
    `${BaseRoute}/session-details/${sessionId}?companyPmsId=${companyPmsId}`,
    {},
    { headers: getAuthHeader(accessToken) }
  )

const AddLinkedCaseAppointment = (
  input: {
    companyPmsId: string
    caseId: string
    matterId: string
    sessionId: string
  },
  accessToken?: string
) =>
  ApiRequest.post(`${BaseRoute}/linked-case-appointments`, input, {
    headers: getAuthHeader(accessToken),
  })

const CheckValidCompany = (companyPmsId: string, accessToken?: string) => {
  return ApiRequest.post(
    `${BaseRoute}/check-valid-company?companyPmsId=${companyPmsId}`,
    {},
    { headers: getAuthHeader(accessToken) }
  )
}

const API = {
  GetMatterDetail,
  GetMatters,
  GetMatterAccount,
  GetPersons,
  GetPmsConfig,
  CreateServices,
  CreateServicesByMatterIdData,
  GetTypeOfClaim,
  GetLookupConfigs,
  GetCentres,
  GetFundingAgreements,
  GetServiceProviders,
  SearchAppointments,
  SearchSpecialists,
  SearchPostcodes,
  UpdateLinkedCaseDetail,
  SendServiceRequest,
  SearchMatterDocuments,
  ImportMatterDocuments,
  HandleLeapConsent,
  GetAllLinkedCases,
  GetLinkedCaseDetail,
  GetCaseCorrespondences,
  CreateCaseCorrespondenceExportLink,
  GetSessionDetail,
  AddLinkedCaseAppointment,
  CheckValidCompany,
}
export default API
